import { useEffect, useState } from "react";
import { useValue } from "@tldraw/state-react";
import { choicesFor } from "./ImportChoices";
import { isPdfFile } from "../shared/drawing-files";
import type { FileMode } from "./file-import-state";
import type { WorkspaceModel } from "./useWorkspace";
import { IMPORT_RECEIPT_MS, importTrayAvailable } from "./import-receipt";

function representation(mode: FileMode, file: Pick<File, "name" | "type">) {
  switch (mode) {
    case "document":
      return "Editable document · original retained";
    case "table":
      return "Editable table · original retained";
    case "grid":
      return "Editable visual grid · original retained";
    case "drawing":
      return "Drawing sheet · original retained";
    case "attachment":
      return "Downloadable original";
    default:
      return `${choicesFor(file).find(([choice]) => choice === mode)?.[1] || "Preview"} · original retained`;
  }
}
function destination(model: WorkspaceModel, parentId: string) {
  if (!parentId.startsWith("shape:")) return "Board canvas";
  const shape = model.editor.getShape(
    parentId as import("@tldraw/tlschema").TLShapeId,
  );
  return shape?.type === "sheet" || shape?.type === "trace"
    ? shape.props.name
    : "Sheet no longer available";
}

export function FileImportTray({ model }: { model: WorkspaceModel }) {
  const q = model.imports,
    unfinished = q.entries.filter((entry) => entry.status !== "Added"),
    needsAttention = unfinished.filter(
      (entry) => entry.status === "Failed" || entry.status === "Paused",
    ).length;
  const [expanded, setExpanded] = useState(false);
  const offscreen = useValue(
    "latest imported file offscreen",
    () => {
      if (!q.lastAdded) return false;
      const bounds = model.editor.getShapePageBounds(q.lastAdded);
      return !!bounds && !model.editor.getViewportPageBounds().contains(bounds);
    },
    [model.editor, q.lastAdded],
  );

  useEffect(() => {
    if (needsAttention || q.error) setExpanded(true);
  }, [needsAttention, q.error]);

  // A successful import is a short-lived receipt, not a camera-position alert
  // that can reappear much later in an unrelated task.
  useEffect(() => {
    if (!q.lastAdded) return;
    const timer = window.setTimeout(q.clearLastAdded, IMPORT_RECEIPT_MS);
    return () => window.clearTimeout(timer);
  }, [q.lastAdded]);

  // The current precision/crop action owns the bottom action area. Import
  // failures remain queued and become visible again when that task ends.
  if (!importTrayAvailable(model.tool, model.cropping)) return null;

  if (!unfinished.length && !q.error)
    return offscreen ? (
      <div className="file-import-alert floating-surface">
        <span>File added</span>
        <button
          onClick={() => {
            const bounds =
              q.lastAdded && model.editor.getShapePageBounds(q.lastAdded);
            if (bounds) model.editor.zoomToBounds(bounds, { inset: 160 });
            q.clearLastAdded();
          }}
        >
          Show file
        </button>
        <button onClick={q.clearLastAdded}>Dismiss</button>
      </div>
    ) : null;
  const current = unfinished.find(
      (entry) => entry.status === "Saving" || entry.status === "Importing",
    ),
    attentionEntry =
      !q.error && needsAttention === 1 && unfinished.length === 1
        ? unfinished[0]
        : undefined,
    resumable = unfinished.filter(
      (entry) => entry.status === "Ready" || entry.status === "Paused",
    ),
    showResumeAll =
      resumable.length > 1 ||
      resumable.some((entry) => entry.status === "Ready"),
    summary = q.error
      ? "File import needs attention"
      : needsAttention
        ? `${needsAttention} file${needsAttention === 1 ? " needs" : "s need"} attention`
        : q.busy
          ? `Adding ${current?.name || `${unfinished.length} files`}…`
          : `${unfinished.length} file${unfinished.length === 1 ? "" : "s"} ready to resume`;

  if (attentionEntry) {
    const file = { name: attentionEntry.name, type: attentionEntry.mime },
      canAddPreview =
        attentionEntry.status === "Failed" &&
        attentionEntry.mode !== "file" &&
        choicesFor(file).some(([mode]) => mode === "file") &&
        /choose a (?:file|reference) preview/i.test(attentionEntry.error || ""),
      needsNewFile =
        !canAddPreview &&
        /not a readable|file is empty|file exceeds|invalid|corrupt/i.test(
          attentionEntry.error || "",
        );
    return (
      <section
        className="file-import-alert floating-surface"
        aria-label="File import needs attention"
      >
        <div className="file-import-alert-copy" role="alert">
          <strong title={attentionEntry.name}>{attentionEntry.name}</strong>
          <small>{representation(attentionEntry.mode, file)}</small>
          <small>
            Destination: {destination(model, attentionEntry.placement.parentId)}
          </small>
          <small>{attentionEntry.error || "Import paused."}</small>
        </div>
        <div className="file-import-actions">
          {needsNewFile ? (
            <button onClick={model.openFilePicker}>Choose another file</button>
          ) : (
            <button
              className={canAddPreview ? "primary" : undefined}
              disabled={q.busy}
              onClick={() => {
                if (canAddPreview)
                  q.configure(attentionEntry.id, { mode: "file" });
                void q.retry(attentionEntry.id);
              }}
            >
              {canAddPreview
                ? "Add as preview"
                : attentionEntry.hasResult
                  ? "Restore object"
                  : "Retry"}
            </button>
          )}
          <button
            disabled={q.busy}
            onClick={() => void q.remove(attentionEntry.id)}
          >
            Dismiss
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className="file-import-tray floating-surface"
      aria-label="File imports"
    >
      <button
        className="file-import-tray-heading"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <span role="status">{summary}</span>
        <span>{expanded ? "Hide" : "Details"}</span>
      </button>
      {expanded && (
        <div className="file-import-tray-body">
          {q.error && (
            <div className="file-import-error" role="alert">
              <span>{q.error}</span>
              <button onClick={() => q.setError("")}>Dismiss</button>
            </div>
          )}
          <ul>
            {unfinished.map((entry) => {
              const file = { name: entry.name, type: entry.mime },
                choices = choicesFor(file),
                configurable =
                  !q.busy &&
                  (entry.status === "Ready" ||
                    entry.status === "Paused" ||
                    entry.status === "Failed");
              return (
                <li key={entry.id}>
                  <strong title={entry.name}>{entry.name}</strong>
                  <small>{entry.error || entry.status}</small>
                  <small>{representation(entry.mode, file)}</small>
                  <small>
                    Destination: {destination(model, entry.placement.parentId)}
                  </small>
                  {entry.saved && (
                    <small>Saved on this device for recovery</small>
                  )}
                  {configurable &&
                    (choices.length > 1 ||
                      isPdfFile(file) ||
                      entry.mode === "table" ||
                      entry.mode === "grid") && (
                      <details>
                        <summary>Import options</summary>
                        {choices.length > 1 && (
                          <label>
                            Import as
                            <select
                              value={entry.mode}
                              onChange={(event) =>
                                q.configure(entry.id, {
                                  mode: event.target.value as FileMode,
                                })
                              }
                            >
                              {choices.map(([value, label]) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                        {isPdfFile(file) && entry.mode !== "attachment" && (
                          <label>
                            Starting page
                            <input
                              type="number"
                              min={1}
                              step={1}
                              value={entry.page}
                              onChange={(event) =>
                                q.configure(entry.id, {
                                  page: Number(event.target.value),
                                })
                              }
                            />
                          </label>
                        )}
                        {(entry.mode === "table" || entry.mode === "grid") && (
                          <>
                            <label className="check">
                              <input
                                type="checkbox"
                                checked={entry.headers}
                                onChange={(event) =>
                                  q.configure(entry.id, {
                                    headers: event.target.checked,
                                  })
                                }
                              />
                              First row is a header
                            </label>
                            {/\.xlsx?$/i.test(entry.name) && (
                              <label>
                                Worksheet number
                                <input
                                  type="number"
                                  min={1}
                                  max={16}
                                  value={entry.worksheet}
                                  onChange={(event) =>
                                    q.configure(entry.id, {
                                      worksheet: Number(event.target.value),
                                    })
                                  }
                                />
                              </label>
                            )}
                          </>
                        )}
                      </details>
                    )}
                  <div className="file-import-actions">
                    {configurable && entry.status !== "Ready" && (
                      <button onClick={() => void q.retry(entry.id)}>
                        {entry.hasResult ? "Restore object" : "Retry"}
                      </button>
                    )}
                    <button
                      disabled={q.busy}
                      onClick={() => void q.remove(entry.id)}
                    >
                      Forget
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          {q.busy ? (
            <button className="full" onClick={q.pause}>
              Pause
            </button>
          ) : showResumeAll ? (
            <button className="primary full" onClick={() => void q.run()}>
              Resume unfinished files
            </button>
          ) : null}
        </div>
      )}
    </section>
  );
}
